import { Link as TanstackLink } from '@tanstack/react-router'
import { ReactNode } from 'react'

import { ExternalLinkIcon } from '@/components/icon'
import {
  Badge,
  Box,
  BreadcrumbCurrentLink,
  BreadcrumbLink,
  BreadcrumbRoot,
  Button,
  For,
  Grid,
  HStack,
  Link,
  Stack,
  Text,
} from '@/components/ui'
import {
  SegmentedControlsContent,
  SegmentedControlsList,
  SegmentedControlsRoot,
  SegmentedControlsTrigger,
} from '@/components/ui/segmented-controls'
import { EXTERNAL_LINKS } from '@/constants/externalLinks'
import {
  CLINICAL_TRIAL_TYPE_LABELS,
  RESEARCH_CATEGORY_LABELS,
  TRIAL_PROGRESS_LABELS,
} from '@/constants/trialDetailLabels'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import { formatDate, lastUpdatedLabel } from '@/utils/datetime'
import { parseChickenSchema } from '@/utils/parseChickenSchema'

import { CriteriaViewerDrawer } from './CriteriaViewerDrawer'

type TrialDescriptionData = {
  trialNumber: string
  trialName: string | null | undefined
  plainTrialName: string | null | undefined
  criteria: string | null | undefined
  principalInvestigatorName: string | null | undefined
  drName: string | null | undefined
  principalInvestigatorOrganization: string | null | undefined
  trialObjective: string | null | undefined
  principalInvestigatorComment: string | null | undefined
  trialPhase: string | null | undefined
  targetDiseaseName: string | null | undefined
  trialSite: string | null | undefined
  openCaseCount: string | null | undefined
  lastCountUpdated: string | null | undefined
  genericDrugName: string | null | undefined
  jrctNumber: string | null | undefined
}

type TrialContactData = {
  contactName: string | null | undefined
  contactOrganization: string | null | undefined
}

type TrialDetailContents = TrialDescriptionData | TrialContactData

type DisplayItemInput<T extends TrialDetailContents> = {
  key: keyof T
  label: string
  renderContent?: (data: T) => ReactNode
}

type DisplayItem<T extends TrialDetailContents> = {
  key: keyof T
  label: string
  renderContent: (data: T) => ReactNode
}

const defaultRenderContent = <T extends TrialDetailContents>(
  key: keyof T,
  data: T,
): ReactNode => {
  const value = data[key]
  if (!value) {
    return null
  }
  return (
    <Text whiteSpace="pre-wrap" overflowWrap="anywhere">
      {String(value)}
    </Text>
  )
}

const createDisplayItems = <T extends TrialDetailContents>(
  items: DisplayItemInput<T>[],
): DisplayItem<T>[] => {
  return items.map(item => ({
    key: item.key,
    label: item.label,
    renderContent:
      item.renderContent || ((data: T) => defaultRenderContent(item.key, data)),
  }))
}

const TrialDescriptionItems = createDisplayItems<TrialDescriptionData>([
  {
    key: 'trialName',
    label: '研究名称',
    renderContent: data => (
      <Text fontWeight="semibold" whiteSpace="pre-wrap" overflowWrap="anywhere">
        {data.trialName}
      </Text>
    ),
  },
  { key: 'plainTrialName', label: '平易な研究名称' },
  {
    key: 'criteria',
    label: '選択除外基準',
    renderContent: data => {
      if (!data.criteria) return null
      return (
        <CriteriaViewerDrawer
          schema={parseChickenSchema(data.criteria)}
          trialNumber={data.trialNumber}
          trigger={
            <Button size="sm" variant="outline">
              選択除外基準を表示
            </Button>
          }
        />
      )
    },
  },
  { key: 'principalInvestigatorName', label: '研究責任（代表）医師の氏名' },
  { key: 'drName', label: '医師の氏名' },
  {
    key: 'principalInvestigatorOrganization',
    label: '研究責任（代表）医師の所属機関',
  },
  { key: 'trialObjective', label: '研究・治験の目的' },
  { key: 'principalInvestigatorComment', label: '責任医師コメント' },
  { key: 'trialPhase', label: '試験のフェーズ' },
  { key: 'targetDiseaseName', label: '対象疾患名' },
  { key: 'trialSite', label: '実施場所' },
  {
    key: 'openCaseCount',
    label: '募集症例数',
    renderContent: data => (
      <HStack gap={0} alignItems="baseline">
        <Text fontSize="2xl">{data.openCaseCount}</Text>
        <Text>例（最終更新{data.lastCountUpdated}）</Text>
      </HStack>
    ),
  },
  { key: 'genericDrugName', label: '医薬品等の一般名称' },
  {
    key: 'jrctNumber',
    label: 'jRCT番号',
    renderContent: data => {
      if (!data.jrctNumber) return null
      return (
        <Link
          color="text.primary"
          textDecorationColor="text.primary"
          variant="underline"
          href={`${EXTERNAL_LINKS.jrctDomain}${data.jrctNumber}`}
          target="_blank"
        >
          {data.jrctNumber} <ExternalLinkIcon />
        </Link>
      )
    },
  },
])

// 問い合わせ先の項目定義
const TrialContactItems = createDisplayItems<TrialContactData>([
  { key: 'contactName', label: '氏名' },
  { key: 'contactOrganization', label: '所属機関' },
])

type TrialDetailItemProps = {
  label: string
  content: ReactNode
  isFirst?: boolean
}

const TrialDetailItem = ({ label, content, isFirst }: TrialDetailItemProps) => {
  // 値がない場合はlabelも省略
  if (content === null) {
    return null
  }

  return (
    <Grid
      gap={0}
      borderColor="border.gray"
      borderBottomWidth="1px"
      borderTopWidth={isFirst ? '1px' : 'none'}
      alignItems="flex-start"
      templateColumns={{ base: '1fr', desktop: '200px 1fr' }}
    >
      <Box px={{ base: 3, desktop: 4 }} py={{ base: 2, desktop: 3 }}>
        <Text color="text.sub">{label}</Text>
      </Box>
      <Box
        px={{ base: 3, desktop: 4 }}
        py={{ base: 2, desktop: 3 }}
        flexGrow={1}
      >
        {content}
      </Box>
    </Grid>
  )
}

const trialDetailContentFragment = graphql(`
  fragment TrialDetailContent on Trial {
    trialNumber
    isNew
    firstPublishedDate
    lastPublishedDate
    latestDetail {
      progress
      researchCategory
      clinicalTrialType
      trialName
      plainTrialName
      principalInvestigatorName
      drName
      principalInvestigatorOrganization
      trialObjective
      principalInvestigatorComment
      trialPhase
      targetDiseaseName
      trialSite
      genericDrugName
      jrctNumber
      contactName
      contactOrganization
    }
    latestCriteria {
      criteriaSchema {
        schema
      }
    }
    latestCount {
      openCaseCount
      savedAt
    }
  }
`)
type TrialDetailPropsContentProps = {
  detailContent: FragmentType<typeof trialDetailContentFragment>
}

const TrialDetailContent = ({
  detailContent: trialDetail,
}: TrialDetailPropsContentProps) => {
  const detailContent = getFragmentData(trialDetailContentFragment, trialDetail)
  const {
    trialNumber,
    isNew,
    latestCount: count,
    latestDetail: detail,
    latestCriteria: criteria,
    firstPublishedDate,
    lastPublishedDate,
  } = detailContent

  const trialDescriptionData: TrialDescriptionData = {
    trialName: detail.trialName,
    trialNumber: trialNumber,
    plainTrialName: detail.plainTrialName,
    criteria: criteria?.[0].criteriaSchema.schema ?? null, // 一つだけ存在する想定
    principalInvestigatorName: detail.principalInvestigatorName,
    drName: detail.drName,
    principalInvestigatorOrganization: detail.principalInvestigatorOrganization,
    trialObjective: detail.trialObjective,
    principalInvestigatorComment: detail.principalInvestigatorComment,
    trialPhase: detail.trialPhase,
    targetDiseaseName: detail.targetDiseaseName,
    trialSite: detail.trialSite,
    openCaseCount: count.openCaseCount?.toString(),
    lastCountUpdated: lastUpdatedLabel(count.savedAt),
    genericDrugName: detail.genericDrugName,
    jrctNumber: detail.jrctNumber,
  }

  const trialContactData: TrialContactData = {
    contactName: detail.contactName,
    contactOrganization: detail.contactOrganization,
  }

  const renderContent = <T extends TrialDetailContents>(
    item: DisplayItem<T>,
    data: T,
  ): ReactNode => {
    return item.renderContent(data)
  }

  return (
    <Stack
      p={{ base: 4, desktop: 6 }}
      gap={{ base: 4, desktop: 6 }}
      bgColor="white"
      borderRadius="8px"
      boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)"
    >
      <Stack gap={2}>
        <HStack gap={4}>
          <HStack gap={2}>
            <Text color="text.sub" fontSize="sm">
              試験ID
            </Text>
            <Text color="text.sub" fontSize="sm">
              :
            </Text>
            <Text fontWeight="semibold">{trialNumber}</Text>
          </HStack>
          <HStack gap={2}>
            <HStack gap={2}>
              {isNew && (
                <Badge colorPalette="red" variant="outline">
                  新規
                </Badge>
              )}
              {detail.progress && (
                <Badge colorPalette="gray" variant="outline">
                  {TRIAL_PROGRESS_LABELS[detail.progress]}
                </Badge>
              )}
            </HStack>
          </HStack>
        </HStack>

        <Stack
          direction={{ base: 'column', desktop: 'row' }}
          gap={{ base: 1, desktop: 6 }}
          gapY={{ desktop: 1 }}
          flexWrap={{ desktop: 'wrap' }}
        >
          {detail.researchCategory && (
            <HStack gap={2}>
              <Text color="text.sub" fontSize="sm">
                研究の種別
              </Text>
              <Text color="text.sub" fontSize="sm">
                :
              </Text>
              <Text fontSize="sm">
                {RESEARCH_CATEGORY_LABELS[detail.researchCategory]}
              </Text>
            </HStack>
          )}

          {detail.clinicalTrialType && (
            <HStack gap={2}>
              <Text color="text.sub" fontSize="sm">
                治験の区分
              </Text>
              <Text color="text.sub" fontSize="sm">
                :
              </Text>
              <Text fontSize="sm">
                {CLINICAL_TRIAL_TYPE_LABELS[detail.clinicalTrialType]}
              </Text>
            </HStack>
          )}

          {firstPublishedDate && (
            <HStack gap={2}>
              <Text color="text.sub" fontSize="sm">
                初回公開日
              </Text>
              <Text color="text.sub" fontSize="sm">
                :
              </Text>
              <Text fontSize="sm">{formatDate(firstPublishedDate)}</Text>
            </HStack>
          )}

          {lastPublishedDate && (
            <HStack gap={2}>
              <Text color="text.sub" fontSize="sm">
                最終公開日
              </Text>
              <Text color="text.sub" fontSize="sm">
                :
              </Text>
              <Text fontSize="sm">{formatDate(lastPublishedDate)}</Text>
            </HStack>
          )}
        </Stack>
      </Stack>

      <Stack gap={0}>
        <For each={TrialDescriptionItems}>
          {(item, index) => (
            <TrialDetailItem
              key={index}
              isFirst={index === 0}
              label={item.label}
              content={renderContent(item, trialDescriptionData)}
            />
          )}
        </For>
      </Stack>

      {/* 表示する項目が1つ以上ある場合のみ表示する */}
      {Object.values(trialContactData).some(Boolean) && (
        <Stack gap={4}>
          <Text fontWeight="bold">問い合わせ先窓口</Text>
          <Stack gap={0}>
            <For each={TrialContactItems}>
              {(item, index) => (
                <TrialDetailItem
                  key={index}
                  isFirst={index === 0}
                  label={item.label}
                  content={renderContent(item, trialContactData)}
                />
              )}
            </For>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

const trialDetailFragment = graphql(`
  fragment TrialDetail on Trial {
    trialNumber
    ...TrialDetailContent
  }
`)
type TrialDetailProps = {
  trialDetail: FragmentType<typeof trialDetailFragment>
}

export const TrialDetail = (props: TrialDetailProps) => {
  const trialDetail = getFragmentData(trialDetailFragment, props.trialDetail)

  return (
    <Stack
      h="full"
      px={{ base: 0, desktop: 6 }}
      py={4}
      gap={2}
      bgColor="blue.50"
      borderRight="1px solid"
      borderColor="border.gray"
      overflow="auto"
    >
      <BreadcrumbRoot px={{ base: 4, desktop: 0 }}>
        <BreadcrumbLink color="status.primary" as="span">
          <TanstackLink to="/trials">試験一覧</TanstackLink>
        </BreadcrumbLink>
        <BreadcrumbCurrentLink>{trialDetail.trialNumber}</BreadcrumbCurrentLink>
      </BreadcrumbRoot>

      <TrialDetailContent detailContent={trialDetail} />
    </Stack>
  )
}

type SPTrialDetailProps = TrialDetailProps & {
  relatedTrialArea: ReactNode
  inquiryArea: ReactNode
}

export const SPTrialDetail = ({
  trialDetail,
  relatedTrialArea,
  inquiryArea,
}: SPTrialDetailProps) => {
  const trialData = getFragmentData(trialDetailFragment, trialDetail)

  return (
    <Stack
      h="full"
      px={{ base: 0, desktop: 6 }}
      pt={4}
      gap={2}
      bgColor="blue.50"
    >
      <BreadcrumbRoot px={{ base: 4, desktop: 0 }}>
        <BreadcrumbLink color="status.primary" as="span">
          <TanstackLink to="/trials">試験一覧</TanstackLink>
        </BreadcrumbLink>
        <BreadcrumbCurrentLink>{trialData.trialNumber}</BreadcrumbCurrentLink>
      </BreadcrumbRoot>

      <SegmentedControlsRoot
        mt={2}
        size="md"
        h="full"
        defaultValue="trialDetail"
        display="flex"
        flexDirection="column"
      >
        <SegmentedControlsList w="full" px={4}>
          <SegmentedControlsTrigger
            variant="soft"
            value="trialDetail"
            flex={1}
            justifyContent="center"
          >
            試験詳細
          </SegmentedControlsTrigger>
          {relatedTrialArea && (
            <SegmentedControlsTrigger
              variant="soft"
              value="relatedTrial"
              flex={1}
              justifyContent="center"
            >
              関連試験
            </SegmentedControlsTrigger>
          )}
          <SegmentedControlsTrigger
            variant="soft"
            value="inquiry"
            flex={1}
            justifyContent="center"
          >
            問い合わせ
          </SegmentedControlsTrigger>
        </SegmentedControlsList>

        <SegmentedControlsContent
          bgColor="blue.50"
          flexGrow={1}
          px={2}
          pb={4}
          value="trialDetail"
        >
          <TrialDetailContent detailContent={trialData} />
        </SegmentedControlsContent>
        {relatedTrialArea && (
          <SegmentedControlsContent flexGrow={1} value="relatedTrial">
            {relatedTrialArea}
          </SegmentedControlsContent>
        )}
        <SegmentedControlsContent flexGrow={1} value="inquiry" overflow="auto">
          {inquiryArea}
        </SegmentedControlsContent>
      </SegmentedControlsRoot>
    </Stack>
  )
}
